/**
 * MessageListErrorBoundary - Error boundary for message list components
 *
 * Catches errors in child components and displays a fallback UI
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'primereact/button';
import '../MessageList/MessageList.scss';

class MessageListErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('MessageList Error:', error, errorInfo);
    }

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Reload the page as a fallback
    if (this.props.resetOnError) {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="message-list-error-boundary d-flex flex-column align-items-center justify-content-center p-5">
          <div className="text-center">
            <i className="pi pi-exclamation-triangle text-danger error-icon" />
            <h3 className="mt-3 mb-2">Something went wrong</h3>
            <p className="text-muted mb-4">
              We're sorry, but there was an error loading the message list.
              <br />
              Please try refreshing the page.
            </p>

            <div className="d-flex gap-2 justify-content-center">
              <Button label="Try Again" icon="pi pi-refresh" onClick={this.handleReset} className="p-button-primary" />
              <Button label="Reload Page" icon="pi pi-replay" onClick={() => window.location.reload()} className="p-button-secondary" />
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-details mt-4 text-left">
                <summary className="cursor-pointer text-danger">Error Details (Development Only)</summary>
                <pre className="mt-2 p-3 bg-light border rounded">
                  <strong>Error:</strong> {this.state.error.toString()}
                  {'\n\n'}
                  <strong>Stack:</strong>
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

MessageListErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  onError: PropTypes.func,
  resetOnError: PropTypes.bool,
};

MessageListErrorBoundary.defaultProps = {
  fallback: null,
  onError: null,
  resetOnError: false,
};

export default MessageListErrorBoundary;
